// src/components/report_details/Directories/Directories.tsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { DirectoryDetail } from "../../../types/interfaces";

export interface URLData {
  id: number;
  url: string;
}

interface DirectoriesProps {
  selectedUrl: string | null;
  urls: URLData[];
  onUrlChange: (url: string) => void;
  directoriesData: DirectoryDetail[];
}

const Directories: React.FC<DirectoriesProps> = ({
  selectedUrl,
  urls,
  onUrlChange,
  directoriesData,
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const dataPerPage = 20;

  // 1️⃣ – ordena poniendo primero los sensibles (sensitive === true)
  const sortedDirectories = [...directoriesData].sort((a, b) => {
    if (a.sensitive === b.sensitive) return 0;
    return a.sensitive ? -1 : 1;
  });

  // 2️⃣ – paginación
  const indexOfLast = currentPage * dataPerPage;
  const indexOfFirst = indexOfLast - dataPerPage;
  const currentData = sortedDirectories.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sortedDirectories.length / dataPerPage);

  const handleUrlSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUrlChange(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="flex flex-col space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-lg">
        {/* ——————————————————  título  —————————————————— */}
        <div className="flex items-center mb-6">
          <div className="w-3 h-10 bg-gray-800 mr-4" />
          <h3 className="text-4xl font-bold">{t("directories.title")}</h3>
        </div>

        {/* ——————————————————  selector URL  —————————————————— */}
        {selectedUrl && (
          <>
            <div className="text-gray-700 text-xl">
              <p className="mb-4">{t("directories.description_1")}</p>
              <p className="mb-4">{t("directories.description_2")}</p>
            </div>

            <p className="text-gray-700 text-xl mt-6">
              {t("directories.select_url")}
            </p>
            <div className="mb-4 mt-2">
              <select
                id="url-select"
                value={selectedUrl}
                onChange={handleUrlSelect}
                className="w-full max-w-md p-2 border border-gray-800 bg-gray-800 text-white font-bold rounded-md"
              >
                <option value="" disabled>
                  {t("directories.select_url_placeholder")}
                </option>
                {urls.map((u) => (
                  <option key={u.id} value={u.url}>
                    {u.url}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {/* ——————————————————  tabla  —————————————————— */}
        {sortedDirectories.length > 0 ? (
          <div className="mt-6 overflow-auto">
            <table className="min-w-full bg-white table-auto">
              <thead>
                <tr className="bg-gray-800 text-white">
                  <th className="py-3 px-6 text-left text-sm font-semibold">
                    {t("directories.table.directory")}
                  </th>
                  <th className="py-3 px-6 text-left text-sm font-semibold">
                    {t("directories.table.url")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((item, i) => (
                  <tr
                    key={i}
                    className={`text-gray-700 hover:bg-gray-200 ${
                      item.sensitive
                        ? "bg-red-100"
                        : i % 2 === 0
                        ? "bg-gray-50"
                        : "bg-gray-100"
                    }`}
                  >
                    <td className="py-3 px-6 border font-semibold text-sm">
                      {item.directory}
                    </td>
                    <td className="py-3 px-6 border">{item.full_url}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          selectedUrl && (
            <p className="text-center text-gray-500 mt-4">
              {t("directories.no_directories")}
            </p>
          )
        )}

        {/* ——————————————————  paginación  —————————————————— */}
        {sortedDirectories.length > dataPerPage && (
          <div className="mt-6 flex justify-center items-center space-x-4">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-800 text-white rounded disabled:bg-gray-300 disabled:text-gray-600"
            >
              {t("pagination.previous")}
            </button>
            <span>
              {t("pagination.pageInfo", {
                current: currentPage,
                total: totalPages,
              })}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-gray-800 text-white rounded disabled:bg-gray-300 disabled:text-gray-600"
            >
              {t("pagination.next")}
            </button>
          </div>
        )}

        {/* ——————————————————  aviso  —————————————————— */}
        {selectedUrl && (
          <div className="mt-6 text-gray-700 text-xl">
            {sortedDirectories.length > 0 ? (
              <p>{t("directories.security_advice")}</p>
            ) : (
              <p className="text-red-600">
                {t("directories.no_visible_directories")}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Directories;
